import JobSearchFetch from '@/interface/JobSearchFetch';
import JobSearchFilters from '@/interface/JobSearchFilters';
import SavedSearchData from '@/interface/SavedSearchData';

import { copy, equals, isObjectEmpty } from '@/helpers/object';
import {
  encodeExposedFilterParams,
  getDefaultFilters,
  rawDataToMainFilters,
  replaceSalaryWithProperCountryCodeFromFacets,
} from '@/helpers/job-filters';
import { getJobRequestDefaultParameters } from '@/helpers/job-request';
import { getUserSelectedJob, saveUserSelectedJob } from '@/helpers/user-selections';
import BookmarkedAttrs from '@/interface/BookmarkedAttrs';
import { keywordRecommendationRequest } from '@/app/api/keyword-recommendation';
import { companyRecommendationRequest } from '@/app/api/company-recommendation';
import getFormattedListItem from './_job-list-item-formatter';
import getFormattedFilters from './_job-search-formatted-filters';

const mainFiltersList = ['keywords', 'city', 'radius'];

const formatOptionsItem = ({ name: label, id: value, frequency: number }) => ({
  label,
  value,
  number,
});

const DEFAULT_SIDEBAR_FILTERS_OPENED = {
  salary: true,
  cities: false,
  companyNames: false,
  careerLevels: false,
  channels: false,
  branches: false,
  date: false,
};

const state = () => ({
  isLoading: false,
  isFacetsLoading: false,
  facetsAreLoaded: false,
  isSubmitted: false,

  list: [],
  options: null,
  data: null,

  lastExecutedSearchParams: null,
  lastExecutedSearchId: null,
  searchFiltering: 0,

  fetchRequest: null,
  facetsFetchRequest: null,
  replacedLocations: '[]',
  mainFilters: {},
  extraFilters: {},
  isGeneralFiltersChanged: false,
  rawFiltersToExecuteQuery: null,
  limit: 25,

  sidebarFiltersOpened: copy(DEFAULT_SIDEBAR_FILTERS_OPENED),

  lastSelectedJob: getUserSelectedJob(),
  searchRecommendations: [],
  searchParsedSlug: null,
  facetsBenchmarks: null,
  selectedJobFromLP: null,
  facetsCountryCode: null,
  facetsMinSalaryStep: 0,
  freeJobs: null,
  freeJobsItems: [],
  contentRatedJobs: [], // List of job items for which user has given rating for the job content (the three smileys at the end of job preview)
});

// mutations
const mutations = {
  setLoading(currentState, newValue) {
    currentState.isLoading = newValue;
  },
  setFacetsLoading(currentState, newValue) {
    currentState.isFacetsLoading = newValue;
  },
  setFacetsAreLoaded(currentState, newValue) {
    currentState.facetsAreLoaded = newValue;
  },
  setSubmitted(currentState, newValue) {
    currentState.isSubmitted = newValue;
  },

  setList(currentState, newValue) {
    currentState.list = newValue;
  },
  setReplacedLocations(currentState, newValue) {
    currentState.replacedLocations = copy(newValue);
  },
  setData(currentState, newValue) {
    currentState.data = newValue;
  },
  setOptions(currentState, newValue) {
    currentState.options = newValue;
  },

  setFetchRequest(currentState, request) {
    currentState.fetchRequest = request;
  },
  setFacetsFetchRequest(currentState, request) {
    currentState.facetsFetchRequest = request;
  },
  setMainFilters(currentState, newValue) {
    currentState.mainFilters = {
      ...currentState.mainFilters,
      ...JSON.parse(JSON.stringify(newValue)),
    };
  },
  setExtraFilters(currentState, newValue) {
    currentState.extraFilters = {
      ...currentState.extraFilters,
      ...JSON.parse(JSON.stringify(newValue)),
    };
  },
  clearFilters(currentState) {
    Object.assign(currentState, {
      mainFilters: {},
      extraFilters: {},
    });
  },
  resetSearchIds(currentState) {
    currentState.data = {
      ...copy(currentState.data),
      initialSearchId: undefined,
      refinedSearchId: undefined,
    };
  },

  setLastExecutedSearchParams(currentState, params) {
    currentState.lastExecutedSearchParams = { ...params };
  },
  setLastExecutedSearchId(currentState, value) {
    currentState.lastExecutedSearchId = value;
  },
  setSearchFiltering(currentState, value) {
    currentState.searchFiltering = value;
  },

  setSidebarFiltersOpened(currentState, newValue) {
    currentState.sidebarFiltersOpened = newValue;
  },
  resetSidebarFiltersOpened(currentState) {
    currentState.sidebarFiltersOpened = copy(DEFAULT_SIDEBAR_FILTERS_OPENED);
  },
  setGeneralFilterChanged(currentState, newValue) {
    currentState.isGeneralFiltersChanged = newValue;
  },

  setRawFiltersToExecuteQuery(currentState, newValue) {
    currentState.rawFiltersToExecuteQuery = newValue;
  },

  setPage(currentState, newValue) {
    currentState.page = newValue;
  },

  setLastSelectedJob(currentState, data) {
    saveUserSelectedJob(JSON.stringify(data));
    currentState.lastSelectedJob = data;
  },

  setBookmarked(currentState, { index, item }) {
    currentState.list.splice(index, 1, item);
  },
  setSearchRecommendations(currentState, newValue) {
    currentState.searchRecommendations = newValue;
  },
  setSearchParsedSlug(currentState, newValue) {
    currentState.searchParsedSlug = newValue;
  },
  setFacetsBenchmarks(currentState, newValue) {
    currentState.facetsBenchmarks = newValue;
  },
  setSelectedJobFromLP(currentState, newValue) {
    currentState.selectedJobFromLP = newValue;
  },
  setFacetsCountryCode(currentState, newValue) {
    currentState.facetsCountryCode = newValue;
  },
  setFacetsMinSalaryStep(currentState, newValue) {
    currentState.facetsMinSalaryStep = newValue;
  },
  setFreeJobs(currentState, newValue) {
    currentState.freeJobs = newValue;
  },
  addFreeJobsItem(currentState, newValue) {
    currentState.freeJobsItems.push(newValue);
  },
  addContentRatedJob(currentState, value) {
    currentState.contentRatedJobs.push(value);
  },
};

const actions = {
  getFilters({ state: currentState }, filters: JobSearchFilters) {
    return getFormattedFilters(filters, {
      searchStartIndex: 0,
      searchLimit: currentState.limit,
      origin: 1,
      loadedSavedSearchId: '',
      json: 1,
    });
  },
  async getList({ commit, dispatch }, payload: JobSearchFetch) {
    const { filters } = payload || {};
    const url = payload?.url || this.getters.getApiLink('search.execute_v2');

    const formattedFilters = await dispatch('getFilters', filters);
    commit('setLastExecutedSearchParams', formattedFilters);
    dispatch('jobSavedSearch/setCurrentSavedSearch', formattedFilters, { root: true });

    const cancelSource = this.$api.CancelToken.source();
    const cancel = () => {
      cancelSource.cancel();
    };

    try {
      const request = this.$api.post(url, formattedFilters, {
        cancelToken: cancelSource.token,
      });
      request.cancel = cancel;
      commit('setFetchRequest', request);

      const { data } = await request;

      const { content } = copy(data?.payload || {});

      const list = copy(content.resultListArray || []);
      delete content.resultListArray;

      const formattedList = list.map(getFormattedListItem);

      return {
        content,
        list: formattedList,
        parsedSlug: content.parsedSlug,

        status: data?.status,
        message: data?.message,
      };
    } catch (error) {
      if (!this.$api.isCancel(error)) {
        console.error(error);
      }
      throw error;
    }
  },
  async getFacets({ commit, dispatch }, filters: JobSearchFilters) {
    const formattedFilters = await dispatch('getFilters', filters);

    const cancelSource = this.$api.CancelToken.source();
    const cancel = () => {
      cancelSource.cancel();
    };

    const url = this.getters.getApiLink('search.facets_v2');
    const request = this.$api.post(url, formattedFilters, {
      cancelToken: cancelSource.token,
    });

    request.cancel = cancel;
    commit('setFacetsFetchRequest', request);

    try {
      const { data } = await request;
      const { facets } = data?.payload?.content || {};

      if (facets && Object.keys(facets).length > 0) {
        commit('setFacetsAreLoaded', true);
      }

      commit('setFacetsCountryCode', data?.payload?.content?.countryCode);
      commit('setFacetsMinSalaryStep', facets?.benchmarks?.minSalaryStep);

      return {
        facets,
      };
    } catch (error) {
      if (!this.$api.isCancel(error)) {
        console.error(error);
      }
      throw error;
    }
  },
  fetchList({ commit, dispatch, getters, rootGetters }, filters: JobSearchFilters) {
    commit('setLoading', true);
    commit('setList', []);

    commit('setSearchParsedSlug', null);
    commit('setSearchRecommendations', []);

    const searchIds = copy(getters.searchIds);
    commit('setData', null);
    commit('setLastExecutedSearchId', null);
    commit('setFreeJobs', null);

    const filtersWithRequiredParams = {
      ...getJobRequestDefaultParameters(),
      ...filters,
      ...searchIds,
    };
    return dispatch('getList', { filters: filtersWithRequiredParams })
      .then(({ list, content, parsedSlug }) => {
        if (parsedSlug) {
          commit('setSearchParsedSlug', parsedSlug);
        }

        // free jobs
        if (content.freeJobs) {
          commit('setFreeJobs', content.freeJobs);
          list.forEach((e) => {
            if (e.freeJobs) {
              if (content.freeJobs.type === 'topJobs' && e.freeJobs?.perks) {
                commit('addFreeJobsItem', e);
              }
              if (content.freeJobs.type === 'contingent' && e.freeJobs?.isFreeJobs) {
                commit('addFreeJobsItem', e);
              }
            }
          });
        }

        // JL-16923 hide elements for guest users in SEO pages
        const isLoggedIn = rootGetters['user/isLoggedIn'];
        if (!parsedSlug || isLoggedIn) {
          dispatch('getSearchRecommendations', {
            filters: filtersWithRequiredParams,
            extra: parsedSlug,
          });
        }

        ['facets'].forEach((key) => {
          if (content[key] !== undefined) {
            delete content[key];
          }
        });
        if (content.replacedLocations) {
          commit('setReplacedLocations', content.replacedLocations);
        }
        commit('setList', list);
        commit('setData', content);
        commit('setSearchFiltering', true);

        return copy(list);
      })
      .finally(() => {
        commit('setSubmitted', true);
      });
  },
  fetchFacets({ commit, dispatch, getters }, filters: JobSearchFilters) {
    commit('setFacetsAreLoaded', false);
    commit('setFacetsLoading', true);
    commit('resetSidebarFiltersOpened');
    const searchIds = copy(getters.searchIds);

    const filtersWithRequiredParams = {
      ...getJobRequestDefaultParameters(),
      ...filters,
      ...searchIds,
    };
    return dispatch('getFacets', filtersWithRequiredParams)
      .then(({ facets: options }) => {
        dispatch('setOptions', options);
        dispatch('setSidebarFiltersOpenedFromFilters', getters.filters);
        dispatch('setFiltersLabel', options);
        dispatch('setFacetsBenchmarks', options.benchmarks);
      })
      .catch((error) => {
        if (!this.$api.isCancel(error)) {
          console.error(error);
          throw error;
        }
      });
  },

  async getFacetsFromRawData({ commit, dispatch }, rawData) {
    const cancelSource = this.$api.CancelToken.source();
    const cancel = () => {
      cancelSource.cancel();
    };
    const url = this.getters.getApiLink('search.facets_v2');
    const request = this.$api.post(url, rawData, {
      cancelToken: cancelSource.token,
    });

    request.cancel = cancel;
    commit('setFacetsFetchRequest', request);

    try {
      const { data } = await request;

      const { facets } = data?.payload?.content || {};

      if (facets && Object.keys(facets).length > 0) {
        commit('setFacetsAreLoaded', true);
      }

      commit('setFacetsCountryCode', data?.payload?.content?.countryCode);
      commit('setFacetsMinSalaryStep', facets?.benchmarks?.minSalaryStep);

      return {
        facets,
      };
    } catch (error) {
      if (!this.$api.isCancel(error)) {
        console.error(error);
      }
      throw error;
    }
  },
  async getListFromRawData({ commit, dispatch }, rawData) {
    const url = this.getters.getApiLink('search.execute_v2');

    commit('setLastExecutedSearchParams', rawData);

    // @ts-ignore
    if (window.appSetup?.user?.firstLoginAfterActivationByUser) {
      rawData.firstSearchAfterActivation = 1;
    }
    dispatch('jobSavedSearch/setCurrentSavedSearch', rawData, { root: true });

    const cancelSource = this.$api.CancelToken.source();
    const cancel = () => {
      cancelSource.cancel();
    };
    // temp
    // rawData.keywords = '';

    try {
      let request;
      if (rawData.slug && rawData.locale && rawData.location_country) {
        const getParams = [
          's=' + rawData.slug,
          'c=' + rawData.location_country,
          'lc=' + rawData.locale,
          'si=' + (rawData.searchStartIndex || 0),
          'l=' + (rawData.searchLimit || 25),
        ];
        request = this.$api.get(url + '?' + getParams.join('&'), {
          cancelToken: cancelSource.token,
        });
      } else {
        request = this.$api.post(url, rawData, {
          cancelToken: cancelSource.token,
        });
      }
      request.cancel = cancel;
      commit('setFetchRequest', request);

      const { data } = await request;

      // mock archived first job
      // data.payload.content.resultListArray[0][10] = '565a180de585ea10ad0c115e088f6ca9';
      // data.payload.content.resultListArray[0][13] = 'e565a180de585ea10ad0c115e088f6ca9';

      // const data = {"status":"OK","message":null,"payload":{"type":"json","content":{"message":null,"numberOfJobsFound":"5","unformattedNumberOfJobsFound":5,"isTotalJobsNumberDisplayInASeparateContainer":true,"filterByCompanyIds":[],"filterByCompanyNames":[],"filterByRecruiterType":false,"filterByRecruiterTypes":[],"resultListArray":[{"0":"jobListRow 0.95","1":"extern-free","2":"","3":"","4":"","5":"Lead QA JavaScript Engineer / NYC Startup","6":"Motion Recruitment Partners LLC","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u0022150000-200000-USD\u0022\u003EUSD 150,000 - 200,000\u003C/span\u003E","8":"New York","9":"/job/view/e66a388fa9cf9a6d31a802464d0621194","10":"66a388fa9cf9a6d31a802464d0621194","11":"","12":"30+ days ago","13":"e66a388fa9cf9a6d31a802464d0621194","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":true}},{"0":"jobListRow 0.92","1":"extern-free","2":"","3":"","4":"","5":"Software Quality Engineer - Python - Fully Remote","6":"Motion Recruitment Partners LLC","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u0022150000-200000-USD\u0022\u003EUSD 150,000 - 200,000\u003C/span\u003E","8":"New York","9":"/job/view/eff49aa61e9f51750b56730b97cbad076","10":"ff49aa61e9f51750b56730b97cbad076","11":"","12":"30+ days ago","13":"eff49aa61e9f51750b56730b97cbad076","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false}},{"0":"jobListRow 0.89","1":"extern-free","2":"","3":"","4":"","5":"Customer Care Associate I","6":"Integrated Resources, Inc.","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-USD\u0022\u003EUSD 60,000 - 80,000\u003C/span\u003E","8":"Latham (NY)","9":"/job/view/eb13d159b26a1c5d009fe5fdf6e515a87","10":"b13d159b26a1c5d009fe5fdf6e515a87","11":"","12":"30+ days ago","13":"eb13d159b26a1c5d009fe5fdf6e515a87","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false}},{"0":"jobListRow 0.84","1":"extern-free","2":"","3":"","4":"","5":"Project Cost Specialist","6":"Randstad USA","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-USD\u0022\u003EUSD 60,000 - 80,000\u003C/span\u003E","8":"Akron (\u00cele-de-France)","9":"/job/view/ebc64afc008c52b8bd0ecf1930e52b5ef","10":"bc64afc008c52b8bd0ecf1930e52b5ef","11":"","12":"30+ days ago","13":"ebc64afc008c52b8bd0ecf1930e52b5ef","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false}},{"0":"jobListRow 0.84","1":"extern-free","2":"","3":"","4":"","5":"R\u0026D Scientist (Protein Analysis)","6":"Hyper Recruitment Solutions Ltd","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-USD\u0022\u003EUSD 60,000 - 80,000\u003C/span\u003E","8":"\u003Cstrong\u003E34 miles\u003C/strong\u003E Berkshire (Regi\u00f3n de Muria)","9":"/job/view/e64f90890abb8438ab48ed5834daa51a4","10":"64f90890abb8438ab48ed5834daa51a4","11":"","12":"30+ days ago","13":"e64f90890abb8438ab48ed5834daa51a4","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false}}],"initialSearchId":115,"refinedSearchId":"","locationsInFilter":0,"branchesInFilter":0,"channelsInFilter":0,"benchmarksInFilter":0,"careerLevelsInFilter":0,"companiesInFilter":0,"recruiterTypesInFilter":0,"minSalary":60000,"maxSalary":-1,"replacedLocations":"[]","numberOfJobsFoundMessage":"jobs found","localizedNumberOfResults":"5 jobs found","freeJobs":{"hasFreeJobs":true,"totalCount":3,"usedCount":2,"type":"contingent"},"parsedSlug":null}}}
      // const data = {"status":"OK","message":null,"payload":{"type":"json","content":{"message":null,"numberOfJobsFound":"5","unformattedNumberOfJobsFound":5,"isTotalJobsNumberDisplayInASeparateContainer":true,"filterByCompanyIds":[],"filterByCompanyNames":[],"filterByRecruiterType":false,"filterByRecruiterTypes":[],"resultListArray":[{"0":"jobListRow markedJob 0.95","1":"extern-free","2":"","3":"","4":"","5":"Lead QA JavaScript Engineer / NYC Startup","6":"Motion Recruitment Partners LLC","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u0022150000-200000-USD\u0022\u003EUSD 150,000 - 200,000\u003C/span\u003E","8":"New York","9":"/job/view/e66a388fa9cf9a6d31a802464d0621194","10":"66a388fa9cf9a6d31a802464d0621194","11":"","12":"30+ days ago","13":"e66a388fa9cf9a6d31a802464d0621194","14":true,"15":"headhunter","16":true,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false,"perks":["Expires soon","High chances: few applications"]}},{"0":"jobListRow 0.92","1":"extern","2":"","3":"","4":"","5":"Software Quality Engineer - Python - Fully Remote","6":"For Premium Members only","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u0022150000-200000-USD\u0022\u003EUSD 150,000 - 200,000\u003C/span\u003E","8":"New York","9":"/job/view/eff49aa61e9f51750b56730b97cbad076","10":"ff49aa61e9f51750b56730b97cbad076","11":"","12":"30+ days ago","13":"eff49aa61e9f51750b56730b97cbad076","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false,"perks":["Great place to work","Urgently required"]}},{"0":"jobListRow 0.89","1":"extern","2":"","3":"","4":"","5":"Customer Care Associate I","6":"For Premium Members only","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-USD\u0022\u003EUSD 60,000 - 80,000\u003C/span\u003E","8":"Latham (NY)","9":"/job/view/eb13d159b26a1c5d009fe5fdf6e515a87","10":"b13d159b26a1c5d009fe5fdf6e515a87","11":"","12":"30+ days ago","13":"eb13d159b26a1c5d009fe5fdf6e515a87","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":false,"perks":["Expires soon","Attractive total compensation"]}},{"0":"jobListRow 0.84","1":"extern-free","2":"","3":"","4":"","5":"Project Cost Specialist","6":"Randstad USA","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-USD\u0022\u003EUSD 60,000 - 80,000\u003C/span\u003E","8":"Akron (\u00cele-de-France)","9":"/job/view/ebc64afc008c52b8bd0ecf1930e52b5ef","10":"bc64afc008c52b8bd0ecf1930e52b5ef","11":"","12":"30+ days ago","13":"ebc64afc008c52b8bd0ecf1930e52b5ef","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":true}},{"0":"jobListRow 0.84","1":"extern-free","2":"","3":"","4":"","5":"R\u0026D Scientist (Protein Analysis)","6":"Hyper Recruitment Solutions Ltd","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-USD\u0022\u003EUSD 60,000 - 80,000\u003C/span\u003E","8":"\u003Cstrong\u003E34 miles\u003C/strong\u003E Berkshire (Regi\u00f3n de Muria)","9":"/job/view/e64f90890abb8438ab48ed5834daa51a4","10":"64f90890abb8438ab48ed5834daa51a4","11":"","12":"30+ days ago","13":"e64f90890abb8438ab48ed5834daa51a4","14":true,"15":"headhunter","16":false,"isCompanyRevealed":true,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0,"freeJobs":{"isFreeJobs":true}}],"initialSearchId":103,"refinedSearchId":"","locationsInFilter":0,"branchesInFilter":0,"channelsInFilter":0,"benchmarksInFilter":0,"careerLevelsInFilter":0,"companiesInFilter":0,"recruiterTypesInFilter":0,"minSalary":60000,"maxSalary":-1,"replacedLocations":"[]","numberOfJobsFoundMessage":"jobs found","localizedNumberOfResults":"5 jobs found","freeJobs":{"hasFreeJobs":false,"totalCount":3,"usedCount":1,"type":"topJobs"},"parsedSlug":null}}}
      // const data = {"status":"OK","message":null,"payload":{"type":"json","content":{"message":null,"numberOfJobsFound":"1,431","unformattedNumberOfJobsFound":1431,"isTotalJobsNumberDisplayInASeparateContainer":true,"filterByCompanyIds":[],"filterByCompanyNames":[],"filterByRecruiterType":false,"filterByRecruiterTypes":[],"resultListArray":[{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Sales (12) Apply Sales filter","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Hamburg","9":"/job/view/e444fb15e6b66f3c5c07a00d3563b5843","10":"444fb15e6b66f3c5c07a00d3563b5843","11":"","12":"30+ days ago","13":"e444fb15e6b66f3c5c07a00d3563b5843","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"E Commerce Manager","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"D\u00fcsseldorf","9":"/job/view/eabe5693750b43190fad5d4055a5cdfa7","10":"abe5693750b43190fad5d4055a5cdfa7","11":"","12":"30+ days ago","13":"eabe5693750b43190fad5d4055a5cdfa7","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Change Manager","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Mannheim","9":"/job/view/e24a457f49b7a61f517ca36ff90c8076d","10":"24a457f49b7a61f517ca36ff90c8076d","11":"","12":"30+ days ago","13":"e24a457f49b7a61f517ca36ff90c8076d","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Teamleiter Sales Finance","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Frankfurt am Main","9":"/job/view/e5d396c83cbc63f51f9ba34775cb70e12","10":"5d396c83cbc63f51f9ba34775cb70e12","11":"","12":"30+ days ago","13":"e5d396c83cbc63f51f9ba34775cb70e12","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Il manager perfetto- Deve aver 3 qualit\u00e0 imprescindibili","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Waterloo","9":"/job/view/ea2219d4360b56e77ca44ffb770bebfb6","10":"a2219d4360b56e77ca44ffb770bebfb6","11":"","12":"30+ days ago","13":"ea2219d4360b56e77ca44ffb770bebfb6","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"New Business Development (12) Apply New Business Development filter","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Berlin","9":"/job/view/e9ebd505105c1e9f482695f5b26a94c0d","10":"9ebd505105c1e9f482695f5b26a94c0d","11":"","12":"30+ days ago","13":"e9ebd505105c1e9f482695f5b26a94c0d","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"B2B Sales (194) Apply B2B Sales filter","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Berlin","9":"/job/view/ee4d858414e3431f1b2c5206971c5a325","10":"e4d858414e3431f1b2c5206971c5a325","11":"","12":"30+ days ago","13":"ee4d858414e3431f1b2c5206971c5a325","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"SAP Change Manager / Junior SAP Projektleiter (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Bayreuth","9":"/job/view/ed0808aacacb5d5db720b8f48af216d89","10":"d0808aacacb5d5db720b8f48af216d89","11":"","12":"30+ days ago","13":"ed0808aacacb5d5db720b8f48af216d89","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Senior Berater oder Manager SAP EH\u0026S (m/w)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-EUR\u0022\u003EEUR 60,000 - 80,000\u003C/span\u003E","8":"Bayreuth","9":"/job/view/ea91fb79d80cc43ea69bca1f75009fe13","10":"a91fb79d80cc43ea69bca1f75009fe13","11":"","12":"30+ days ago","13":"ea91fb79d80cc43ea69bca1f75009fe13","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Senior Berater oder Manager SAP PS/CS (m/w)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-EUR\u0022\u003EEUR 60,000 - 80,000\u003C/span\u003E","8":"Bayreuth","9":"/job/view/e659a99c6a6f7be1a939bd97b1c010f01","10":"659a99c6a6f7be1a939bd97b1c010f01","11":"","12":"30+ days ago","13":"e659a99c6a6f7be1a939bd97b1c010f01","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Senior IT Management (9) Apply Senior IT Management filter","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002280000-100000-EUR\u0022\u003EEUR 80,000 - 100,000\u003C/span\u003E","8":"Frankfurt am Main","9":"/job/view/ead17d5b5b16674ed6013529ef1d22d08","10":"ad17d5b5b16674ed6013529ef1d22d08","11":"","12":"30+ days ago","13":"ead17d5b5b16674ed6013529ef1d22d08","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"IT Service Manager (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Seesen, Goslar","9":"/job/view/e23ed8f2d249249a6507f7f6b9dc0fa1c","10":"23ed8f2d249249a6507f7f6b9dc0fa1c","11":"","12":"30+ days ago","13":"e23ed8f2d249249a6507f7f6b9dc0fa1c","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Project Manager at payever (m/f/x), Hamburg","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Hamburg","9":"/job/view/efb6479de86b99bc09eb96f10f41dcc29","10":"fb6479de86b99bc09eb96f10f41dcc29","11":"","12":"30+ days ago","13":"efb6479de86b99bc09eb96f10f41dcc29","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Feel Good Manager (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Regensburg","9":"/job/view/e59094b307298ebe0a92a870c3857f152","10":"59094b307298ebe0a92a870c3857f152","11":"","12":"30+ days ago","13":"e59094b307298ebe0a92a870c3857f152","14":false,"15":"company","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 1","1":"extern","2":"","3":"","4":"","5":"Model Manager","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Gleichen","9":"/job/view/e9883ba69130ed09e95805dc06c94fa2e","10":"9883ba69130ed09e95805dc06c94fa2e","11":"","12":"30+ days ago","13":"e9883ba69130ed09e95805dc06c94fa2e","14":false,"15":"company","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"HR Manager IT-Beratung (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-EUR\u0022\u003EEUR 60,000 - 80,000\u003C/span\u003E","8":"Hamburg","9":"/job/view/e776ae8dbd73f3adc315732363863ac4f","10":"776ae8dbd73f3adc315732363863ac4f","11":"","12":"30+ days ago","13":"e776ae8dbd73f3adc315732363863ac4f","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"E-Mail-Marketing-Manager (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Elmshorn","9":"/job/view/eee025c3c3dbe250975865dce7abe111b","10":"ee025c3c3dbe250975865dce7abe111b","11":"","12":"30+ days ago","13":"eee025c3c3dbe250975865dce7abe111b","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Area Sales Manager (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Hamburg","9":"/job/view/ebebc2253062e5045b1f4e47fc8ba82d4","10":"bebc2253062e5045b1f4e47fc8ba82d4","11":"","12":"30+ days ago","13":"ebebc2253062e5045b1f4e47fc8ba82d4","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Application-Engineer/-Manager (m/w/d) + 500\u20ac Willkommenspr\u00e4mie","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-EUR\u0022\u003EEUR 60,000 - 80,000\u003C/span\u003E","8":"Tettnang","9":"/job/view/e58d2e54772c9cafbfd7306c8096e865f","10":"58d2e54772c9cafbfd7306c8096e865f","11":"","12":"30+ days ago","13":"e58d2e54772c9cafbfd7306c8096e865f","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Junior Produkt Manager (m/w/d) - ab 13,00\u20ac Stundenlohn","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Pulheim","9":"/job/view/ef55a85d1b11da6e7c7b0d747ee4a963f","10":"f55a85d1b11da6e7c7b0d747ee4a963f","11":"","12":"30+ days ago","13":"ef55a85d1b11da6e7c7b0d747ee4a963f","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Personal Manager Rekrutierung (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Berlin","9":"/job/view/e98e18bc9421b2acb8f5b97d31d51d6f1","10":"98e18bc9421b2acb8f5b97d31d51d6f1","11":"","12":"30+ days ago","13":"e98e18bc9421b2acb8f5b97d31d51d6f1","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"HR Business Partner / HR Manager (m/w/d)","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002280000-100000-EUR\u0022\u003EEUR 80,000 - 100,000\u003C/span\u003E","8":"Brunsb\u00fcttel","9":"/job/view/e537592d065535f2f0b33e9393b40bfd2","10":"537592d065535f2f0b33e9393b40bfd2","11":"","12":"30+ days ago","13":"e537592d065535f2f0b33e9393b40bfd2","14":false,"15":"headhunter","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Project manager_Digital Consulting_Native German","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Hamburg","9":"/job/view/e7aacee8749b16e9070b6e5c97f6cd4b7","10":"7aacee8749b16e9070b6e5c97f6cd4b7","11":"","12":"30+ days ago","13":"e7aacee8749b16e9070b6e5c97f6cd4b7","14":false,"15":"","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Business Development Manager \u2013 IKEA Category Green Plants","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002240000-60000-EUR\u0022\u003EEUR 40,000 - 60,000\u003C/span\u003E","8":"Dortmund","9":"/job/view/ef8e169d971f4c1ea06b20608d151ac32","10":"f8e169d971f4c1ea06b20608d151ac32","11":"","12":"30+ days ago","13":"ef8e169d971f4c1ea06b20608d151ac32","14":false,"15":"jobboard","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0},{"0":"jobListRow 0.99","1":"extern","2":"","3":"","4":"","5":"Senior Quality Auditor (CAMo","6":"Only for registered members","7":"\u003Cspan title=\u0022The salary benchmark is a premium service offered by JobLeads. It is compiled based on the target salaries for market leaders in the relevant sector segments. It is intended to serve as a guide for Premium Members in the assessment of open positions and as a help in salary negotiations. It does not refer to a salary specified by a company, which could be significantly more or less.\u0022 class=\u0022benchmark\u0022 data-salary=\u002260000-80000-EUR\u0022\u003EEUR 60,000 - 80,000\u003C/span\u003E","8":"Osten","9":"/job/view/e411010307a4711fe56fbed2633691ce8","10":"411010307a4711fe56fbed2633691ce8","11":"","12":"30+ days ago","13":"e411010307a4711fe56fbed2633691ce8","14":false,"15":"company","16":false,"isCompanyRevealed":false,"isFeatured":false,"isPromoted":false,"isDeactivated":false,"maxSalary":0}],"initialSearchId":1616022,"refinedSearchId":"","locationsInFilter":0,"branchesInFilter":0,"channelsInFilter":0,"benchmarksInFilter":0,"careerLevelsInFilter":0,"companiesInFilter":0,"recruiterTypesInFilter":0,"minSalary":40000,"maxSalary":-1,"replacedLocations":"[]","numberOfJobsFoundMessage":"jobs found","localizedNumberOfResults":"1,431 jobs found","parsedSlug":null}}};

      const { content } = copy(data?.payload || {});

      const list = copy(content.resultListArray || []);
      delete content.resultListArray;

      const formattedList = list.map(getFormattedListItem);

      return {
        content,
        list: formattedList,
        parsedSlug: content.parsedSlug,

        status: data?.status,
        message: data?.message,
      };
    } catch (error) {
      if (!this.$api.isCancel(error)) {
        console.error(error);
      }
      throw error;
    }
  },
  fetchListFromRawData({ commit, dispatch, getters, rootGetters }, rawData) {
    commit('setLoading', true);
    commit('setList', []);
    commit('setLastExecutedSearchId', rawData.lastExecutedSearch || null);

    commit('setSearchParsedSlug', null);
    commit('setSearchRecommendations', []);
    commit('setFreeJobs', null);

    const searchIds = copy(getters.searchIds);

    commit('setData', null);
    dispatch('setFiltersFromRawData', rawData);

    const rawDataWithSearchIds = { ...rawData, ...searchIds };
    return dispatch('getListFromRawData', rawDataWithSearchIds)
      .then(({ list, content, parsedSlug }) => {
        if (parsedSlug) {
          dispatch('setFilters', {
            keywords: parsedSlug?.keywords,
            city: parsedSlug?.location,
          });
          commit('setSearchParsedSlug', parsedSlug);
        }
        // free jobs
        // content.freeJobs = {
        //   hasFreeJobs: true,
        //   usedCount: 1,
        //   totalCount: 3,
        //   type: 'topJobs',
        // }
        if (content.freeJobs) {
          commit('setFreeJobs', content.freeJobs);
          list.forEach((e) => {
            // e.freeJobs = {
            //   perks: ['Expires soon', 'Fast hiring process'],
            //   isFreeJobs: true
            // }
            if (content.freeJobs.type === 'topJobs' && e.freeJobs?.perks) {
              commit('addFreeJobsItem', e);
            }
            if (content.freeJobs.type === 'contingent' && e.freeJobs?.isFreeJobs) {
              commit('addFreeJobsItem', e);
            }
          });
        }

        // JL-16923 hide elements for guest users in SEO pages
        const isLoggedIn = rootGetters['user/isLoggedIn'];
        if (!parsedSlug || isLoggedIn) {
          dispatch('getSearchRecommendations', {
            filters: rawDataWithSearchIds,
            extra: parsedSlug,
          });
        }

        ['facets'].forEach((key) => {
          if (content[key] !== undefined) {
            delete content[key];
          }
        });

        if (content.replacedLocations) {
          commit('setReplacedLocations', content.replacedLocations);
        }

        commit('setList', list);
        commit('setData', content);
        commit('setSearchFiltering', 1);

        return copy(list);
      })
      .finally(() => {
        commit('setSubmitted', true);
      });
  },
  fetchFacetsFromRawData({ commit, dispatch, getters }, rawData) {
    commit('setFacetsAreLoaded', false);
    commit('setFacetsLoading', true);
    commit('resetSidebarFiltersOpened');

    const searchIds = copy(getters.searchIds);
    const rawDataWithSearchIds = { ...rawData, ...searchIds };

    return dispatch('getFacetsFromRawData', rawDataWithSearchIds)
      .then(({ facets: options }) => {
        dispatch('setOptions', options);
        dispatch('updateExtraFiltersFromRawData', {
          rawData: rawDataWithSearchIds,
          facets: options,
        });
        dispatch('setSidebarFiltersOpenedFromFilters', getters.filters);

        dispatch('setFiltersLabel', options);
        dispatch('setFacetsBenchmarks', options.benchmarks);
      })
      .catch((error) => {
        if (!this.$api.isCancel(error)) {
          console.error(error);
          throw error;
        }
      });
  },
  setFiltersFromRawData({ dispatch }, rawData) {
    dispatch('setFilters', rawDataToMainFilters(rawData));
  },
  updateExtraFiltersFromRawData({ getters, dispatch }, { rawData, facets }) {
    const { formattedOptions } = getters;

    const { companies, locations, recruiterTypes, ...others } = Object.fromEntries(
      Object.entries(facets)
        // @ts-ignore
        .map(([key, value]) => [key, value.selected]),
    );
    const selectedFilterValues = {
      companyNames: companies,
      cities: locations,
      sources: recruiterTypes,
      ...others,
    };

    const extraFilters = Object.fromEntries(
      Object.entries(formattedOptions)
        .map(([key, value]) => {
          const newKey = key.substring(0, key.length - 7);
          const selectedFilters =
            newKey === 'channels'
              ? value
                  // @ts-ignore
                  .map((v) => {
                    const { options, ...val } = v;
                    return {
                      ...val,
                      values: options.filter((option) =>
                        selectedFilterValues[newKey].includes(option.value),
                      ),
                    };
                  })
                  .filter((v) => v.values && v.values.length)
              : value
                  // @ts-ignore
                  .filter((v) => selectedFilterValues[newKey].includes(v.value));

          return [newKey, selectedFilters];
        })
        .filter(([key, value]) => value && value.length),
    );

    const date =
      rawData.filter_by_daysReleased === 31 || rawData.filter_by_daysReleased === ''
        ? undefined
        : rawData.filter_by_daysReleased;

    extraFilters.salary = replaceSalaryWithProperCountryCodeFromFacets({
      minSalary: rawData.minSalary,
      maxSalary: rawData.maxSalary,
    });

    dispatch('setFilters', {
      ...extraFilters,
      date,
    });
  },

  saveFetchData({ commit }, data) {
    const { content, list } = data;

    commit('setList', list);
    commit('setData', content);
  },
  destroy({ commit }) {
    commit('setLoading', true);
    commit('setFacetsAreLoaded', false);
    commit('setFacetsLoading', true);
    commit('setList', []);
    commit('setData', null);
    commit('clearFilters');
    commit('setSearchRecommendations', []);
  },

  setFilters({ state: currentState, commit, getters }, filters) {
    const result = copy(filters);

    // set general filters changed state
    (() => {
      const { mainFilters } = currentState;

      const isChanged = mainFiltersList
        .filter((key) => filters[key] !== undefined)
        .some((key) => JSON.stringify(filters[key]) !== JSON.stringify(mainFilters[key]));

      commit('setGeneralFilterChanged', isChanged);
    })();

    // set main filters
    (() => {
      const newMainFilters = Object.fromEntries(
        Object.entries(filters).filter(([key]) => mainFiltersList.includes(key)),
      );

      commit('setMainFilters', newMainFilters);
    })();

    // set extra filters
    (() => {
      const newExtraFilters = Object.fromEntries(
        Object.entries(filters).filter(([key]) => !mainFiltersList.includes(key)),
      );

      (() => {
        const { salary } = newExtraFilters;
        if (salary === null) {
          delete newExtraFilters.salary;
        }
        if (!salary) return;
        if (!salary[0] || !salary[1]) {
          delete newExtraFilters.salary;
        }
      })();

      commit('setExtraFilters', newExtraFilters);
    })();

    return { ...getters.filters, ...result };
  },
  resetSearchState({ dispatch }) {
    dispatch('resetSearchIds');
    return dispatch('clearFilters');
  },
  clearFilters({ commit }) {
    commit('clearFilters');
    return Promise.resolve();
  },
  resetSearchIds({ commit }) {
    commit('resetSearchIds');
    commit('setLastExecutedSearchId', null);
    commit('setSearchFiltering', 0);
  },
  setOptions({ state: currentState, commit }, data) {
    const keys = {
      branches: 'branches',
      careerLevels: 'careerLevels',
      channels: 'channels',
      companies: 'companyNames',
      locations: 'cities',
      recruiterTypes: 'recruiterTypes',
    };

    let newOptions = Object.entries(data)
      .filter(([key]) => keys[key])
      .map(([key, item]) => {
        const formattedKey = keys[key];
        // @ts-ignore
        const values = Object.entries(item?.facet || {}).map(([, value]) => value);
        let formattedValues = [];

        switch (formattedKey) {
          case 'channels': {
            formattedValues = values.map(({ children, ...atrs }) => ({
              // @ts-ignore
              ...formatOptionsItem(atrs),
              // @ts-ignore
              options: Object.entries(children || {}).map(([, value]) => formatOptionsItem(value)),
            }));
            break;
          }
          default: {
            formattedValues = values.map(formatOptionsItem);
            break;
          }
        }

        return [formattedKey, formattedValues];
      });

    // embed data with previous options
    (() => {
      if (currentState.isGeneralFiltersChanged) return;

      const { options: currentOptions, extraFilters } = currentState;

      // conditions to proceed
      const optionKeys = Object.entries(keys).map(([, key]) => key);
      const selectedOptions = optionKeys.filter((key) => (extraFilters[key] || []).length);
      const isOneGroupFromSelectedList = selectedOptions.length === 1;

      if (!currentOptions || !isOneGroupFromSelectedList) return;

      const [selectedKey] = selectedOptions;

      const compareItem = ({ label, value }, item2) =>
        label === item2.label && value === item2.value;

      const copyOptions = (array1, array2, changeNumber) => {
        const result = array1.reduce((res, _, currentIndex) => {
          const currentItem = res[currentIndex];
          const newItem = array2.find((item) => compareItem(item, currentItem));

          if (currentItem.options !== undefined) {
            if (newItem === undefined) {
              const newInnerOptions = changeNumber
                ? currentItem.options.map((item) => Object.assign(item, { number: 0 }))
                : currentItem.options;

              res.push(
                Object.assign(currentItem, {
                  options: newInnerOptions,
                }),
              );
              return res;
            }

            const { options, ...atrs } = currentItem;
            res.splice(currentIndex, 1, {
              ...atrs,
              options: copyOptions(options, newItem.options, changeNumber),
            });

            return res;
          }

          if (changeNumber) {
            currentItem.number = newItem ? newItem.number : 0;
          }

          return res;
        }, copy(array1));

        return array2.reduce((res, newItem) => {
          const isNewItemExist = res.find((item) => compareItem(item, newItem));

          if (!isNewItemExist) {
            res.push(copy(newItem));
          }

          return result;
        }, result);
      };

      newOptions = copy(newOptions).reduce(
        (res, [key, newItems]) => {
          const existedIndex = res.findIndex(([k]) => k === key);

          if (existedIndex === -1) {
            res.push([key, copy(newItems)]);
          } else {
            const [, currentItems] = res[existedIndex];
            res.splice(existedIndex, 1, [
              key,
              copyOptions(currentItems, newItems, key !== selectedKey),
            ]);
          }

          return res;
        },
        Object.entries(copy(currentOptions)),
      );
    })();

    commit(
      'setOptions',
      newOptions.reduce(
        (r, [key, value]) => ({
          ...r,
          [key]: value,
        }),
        {},
      ),
    );
  },
  setFiltersLabel({ state: currentState, commit }) {
    const { extraFilters, options } = currentState;
    const result = copy(extraFilters);

    ['channels', 'branches'].forEach((key) => {
      const values = result[key];
      const option = options[key];

      if (!values || !option) return;

      const isValuesOk = values.every(({ label, values: vs }) =>
        vs ? vs.every(({ label: lb }) => lb !== undefined) : label !== undefined,
      );

      if (isValuesOk) return;

      values
        .filter(({ values: vs }) => vs === undefined)
        .forEach((filter, index) => {
          const optionItem = option.find((item) => item.value === filter.value);
          if (!optionItem) return;

          const newValue = copy(optionItem);

          if (newValue.options !== undefined) {
            newValue.values = newValue.options;
            delete newValue.options;
          }

          values[index] = newValue;
        });
    });

    if (JSON.stringify(result) === JSON.stringify(extraFilters)) return;
    commit('setExtraFilters', result);
  },

  setSidebarFiltersOpenedFromFilters({ commit }, filters) {
    commit(
      'setSidebarFiltersOpened',
      Object.fromEntries(
        Object.entries(DEFAULT_SIDEBAR_FILTERS_OPENED).map(([key]) => [
          key,
          !!filters[key] || filters[key] === 0,
        ]),
      ),
    );
  },

  setDefaultFilters({ dispatch }) {
    dispatch('setFilters', getDefaultFilters());
  },

  async saveFilters({ state: currentState, dispatch }, data: SavedSearchData) {
    const { name, notification } = data;
    const formattedFilters = await dispatch('getFilters', data);

    /* eslint-disable @typescript-eslint/camelcase */
    const formattedData = {
      ...formattedFilters,
      saved_search_id: 0,
      notification_frequency: notification.value,
      saved_search_name: name,
      initialSearchId: '',
      refinedSearchId: '',
      saveSearchClickOrigin: 'desktopApp',
      isStandardSavedSearch: 0,
    };
    /* eslint-enable @typescript-eslint/camelcase */
    return this.$api.post(this.getters.getApiLink('savedSearch.save'), formattedData);
  },

  setItemBookmarkStatus({ state: currentState, commit }, item: BookmarkedAttrs) {
    const { list } = currentState;
    const index = list.findIndex(({ id }) => id === item.id);

    if (index > -1) {
      const formattedItem = copy(list[index]);
      formattedItem.isBookmarked = item.value;
      commit('setBookmarked', { index, item: formattedItem });
    }
  },

  selectJob({ commit }, job) {
    commit('setLastSelectedJob', job);
  },
  async getSearchRecommendations({ dispatch, commit }, { filters, extra }) {
    if (extra?.companyName) {
      dispatch('getCompanyRecommendations', extra.companyName);
      return;
    }
    const keywords = extra?.keywordOrChannelName || filters?.keywords || extra?.keywords || null;
    if (!keywords) return;
    commit('setSearchRecommendations', []);
    try {
      const results = await keywordRecommendationRequest(keywords);
      if (results && results.length) {
        commit('setSearchRecommendations', results);
      }
    } catch (error) {
      console.error(error);
    }
  },
  async getCompanyRecommendations({ commit }, companyName) {
    commit('setSearchRecommendations', []);
    try {
      const results = await companyRecommendationRequest(companyName);
      if (results && results.length) {
        commit('setSearchRecommendations', results);
      }
    } catch (error) {
      console.error(error);
    }
  },
  setFacetsBenchmarks({ commit }, benchmarks) {
    commit('setFacetsBenchmarks', benchmarks);
  },
};

const getters = {
  isLoading: (currentState) => currentState.isLoading,
  isFacetsLoading: (currentState) => currentState.isFacetsLoading,
  isSubmitted: (currentState) => currentState.isSubmitted,

  list: (currentState) => currentState.list,
  total: (currentState) => currentState.data?.numberOfJobsFound,
  totalNumber: (currentState) => Number(currentState.data?.unformattedNumberOfJobsFound) || 0,
  totalNumberMessage: (currentState) =>
    currentState.data?.localizedNumberOfResults || translate('No job found'),

  filters: (currentState) =>
    encodeExposedFilterParams({
      ...currentState.mainFilters,
      ...currentState.extraFilters,
    }),
  activeFilters: (currentState, currentGetters, rootState, rootGetters) => {
    if (!currentState.facetsAreLoaded) {
      // In case of lazy loading of facets, we don't want to show any active filters until facets are loaded
      return {};
    }

    const savedSearchName = currentGetters.lastExecutedSearchParams?.saved_search_name;
    if (savedSearchName) {
      return {
        searchName: savedSearchName,
      };
    }
    const salaryOptions = currentGetters.salaryOptionsFromFacets;
    const defaultSalary = [salaryOptions[0], salaryOptions[salaryOptions.length - 1]];

    const { salary, country: toSkip, ...otherFilters } = copy(currentState.extraFilters);
    otherFilters.date = undefined;

    if (equals(salary, defaultSalary)) {
      return otherFilters;
    }

    return {
      salary,
      ...otherFilters,
    };
  },
  extraFilters: (s) => s.extraFilters,
  mainFilters: (s) => s.mainFilters,
  isGeneralFiltersChanged: (currentState) => currentState.isGeneralFiltersChanged,

  fetchRequest: (s) => s.fetchRequest,
  facetsFetchRequest: (s) => s.facetsFetchRequest,

  options: (s) => s.options || {},
  formattedOptions: (s) => {
    if (!s.options || isObjectEmpty(s.options)) {
      return {};
    }

    const { recruiterTypesOptions, ...otherFormattedOptions }: any = [
      'branches',
      'careerLevels',
      'channels',
      'companyNames',
      'cities',
      'recruiterTypes',
    ].reduce((r, key) => {
      let result = s.options[key] || [];

      if (key === 'cities') {
        const city = s.filters?.city || '';
        if (city !== '') {
          const cityValues = city.split(',').map((v) => v.trim().toLowerCase());
          result = result.filter(({ label }) => !cityValues.includes(label.toLowerCase()));
        }
      }

      return {
        ...r,
        [`${key}Options`]: result,
      };
    }, {});

    return {
      ...otherFormattedOptions,
      sourcesOptions: recruiterTypesOptions,
    };
  },

  replacedLocations: (currentState) => JSON.parse(currentState.replacedLocations),

  limit: (s) => s.limit,

  rawFiltersToExecuteQuery: (s) => s.rawFiltersToExecuteQuery,

  sidebarFiltersOpened: (s) => s.sidebarFiltersOpened,

  lastSelectedJob: (s) => s.lastSelectedJob,

  lastExecutedSearchParams: (s) => s.lastExecutedSearchParams,
  searchIds: (s) => ({
    initialSearchId: s.data?.initialSearchId,
    refinedSearchId: s.data?.refinedSearchId,

    lastExecutedSearch: s.lastExecutedSearchId,
    searchFiltering: s.searchFiltering,
  }),
  searchRecommendations: (s) => s.searchRecommendations,
  searchParsedSlug: (s) => s.searchParsedSlug,
  facetsBenchmarks: (s) => s.facetsBenchmarks,
  selectedJobFromLP: (s) => s.selectedJobFromLP,
  facetsCountryCode: (s) => s.facetsCountryCode,
  salaryOptionsFromFacets: (currentState, currentGetters, rootState, rootGetters) => {
    const countryValue =
      currentGetters.facetsCountryCode || rootGetters['country/currentCountry'].value;
    const result = rootGetters['salary/getSalaryOptions'](countryValue);
    if (currentState.facetsMinSalaryStep > 0) {
      return result.filter((e, i) => i >= currentState.facetsMinSalaryStep);
    }
    return result;
  },
  freeJobs: (s) => s.freeJobs,
  freeJobsItems: (s) => s.freeJobsItems,
  facetsAreLoaded: (s) => s.facetsAreLoaded,
};

export default {
  namespaced: true,

  mutations,
  state,
  actions,
  getters,
};
